import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { type DateRange } from "react-day-picker";
import { DatePicker } from "@/components/ui/date-picker";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Label } from "@/components/ui/label";
import { OnboardingPlan } from "@forgepoint/shared";
import {
  formatDateOnly,
  parseDateOnly,
} from "@/modules/onboarding/onboardingDates";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";

/**
 * The single place the schedule's three dates are set: the schedule range
 * (start–end) and the go-live date, as popover calendar pickers. Dates
 * persist only on complete selections, so a half-picked range never reaches
 * the API.
 */
export function ScheduleRangeControls() {
  const plan = useOnboardingStore((state) => state.plan);
  const planSaveState = useOnboardingStore((state) => state.planSaveState);
  const savePlan = useOnboardingStore((state) => state.savePlan);

  const [range, setRange] = useState<DateRange | undefined>(
    planScheduleRange(plan),
  );

  useEffect(() => {
    setRange(planScheduleRange(plan));
  }, [plan]);

  const handleRangeSelect = (selected: DateRange | undefined) => {
    setRange(selected);
    if (selected?.from && selected?.to) {
      savePlan({
        scheduleStart: formatDateOnly(selected.from),
        scheduleEnd: formatDateOnly(selected.to),
      });
    }
  };

  const handleGoLiveSelect = (date: Date | undefined) => {
    savePlan({ goLiveDate: date ? formatDateOnly(date) : null });
  };

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-schedule-range">Schedule range</Label>
        <DateRangePicker
          id="onboarding-schedule-range"
          value={range}
          onSelect={handleRangeSelect}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-go-live">Go-live date</Label>
        <DatePicker
          id="onboarding-go-live"
          value={plan?.goLiveDate ? parseDateOnly(plan.goLiveDate) : undefined}
          onSelect={handleGoLiveSelect}
        />
      </div>
      <div className="flex h-10 items-center gap-2">
        {planSaveState.isSaving && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {planSaveState.error && (
          <span className="text-sm text-destructive">
            {planSaveState.error}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Converts the plan's stored schedule range into a calendar DateRange.
 */
function planScheduleRange(plan: OnboardingPlan | null): DateRange | undefined {
  if (!plan?.scheduleStart) {
    return undefined;
  }
  return {
    from: parseDateOnly(plan.scheduleStart),
    to: plan.scheduleEnd ? parseDateOnly(plan.scheduleEnd) : undefined,
  };
}
